import { useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { fetchCommercialContext } from "../services/commercialEntitlementsApi";
import { useAuthStore } from "../stores/authStore";
import { useCommercialEntitlementStore } from "../stores/commercialEntitlementStore";
import { useOrgContextStore } from "../stores/orgContextStore";

const FOCUS_REFRESH_AFTER_MS = 60_000;

function safeErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error) && error.response?.status === 503) {
    return "Plan features are temporarily unavailable. Please try again.";
  }
  return "Unable to load plan features.";
}

export function useCommercialEntitlements() {
  const requestControllerRef = useRef<AbortController | null>(null);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const userId = useAuthStore((state) => state.user?.id);
  const role = useAuthStore((state) => state.user?.role);
  const activeOrganizationId = useOrgContextStore(
    (state) => state.activeOrganizationId
  );
  const storedContext = useCommercialEntitlementStore((state) => state.context);
  const storedScopeKey = useCommercialEntitlementStore(
    (state) => state.scopeKey
  );
  const storedLoading = useCommercialEntitlementStore((state) => state.loading);
  const storedLoaded = useCommercialEntitlementStore((state) => state.loaded);
  const storedLoadedAt = useCommercialEntitlementStore(
    (state) => state.loadedAt
  );
  const storedError = useCommercialEntitlementStore((state) => state.error);

  const isCommercialRole =
    role === "provider" ||
    role === "provider_staff" ||
    role === "social_worker";
  const scopeKey =
    isAuthenticated && isCommercialRole
      ? role === "social_worker"
        ? userId != null
          ? `care-coordinator:${userId}`
          : null
        : activeOrganizationId != null
        ? `provider-organization:${activeOrganizationId}`
        : null
      : null;
  const contextIsCurrent = scopeKey !== null && storedScopeKey === scopeKey;
  const context = contextIsCurrent ? storedContext : null;
  const loading = scopeKey !== null && (!contextIsCurrent || storedLoading);
  const loaded = contextIsCurrent && storedLoaded;
  const error = contextIsCurrent ? storedError : null;

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !isCommercialRole || scopeKey === null) {
      requestControllerRef.current?.abort();
      useCommercialEntitlementStore.getState().clear();
      return;
    }
    const store = useCommercialEntitlementStore.getState();
    if (store.scopeKey === scopeKey && store.loading) return;
    requestControllerRef.current?.abort();
    const controller = new AbortController();
    requestControllerRef.current = controller;
    const requestId = store.setLoading(scopeKey);
    try {
      const payload = await fetchCommercialContext(controller.signal);
      if (!controller.signal.aborted) {
        store.setContext(payload, scopeKey, requestId);
      }
    } catch (requestError) {
      if (!controller.signal.aborted) {
        store.setError(safeErrorMessage(requestError), scopeKey, requestId);
      }
    }
  }, [isAuthenticated, isCommercialRole, scopeKey]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onFocus = () => {
      const state = useCommercialEntitlementStore.getState();
      if (
        scopeKey !== null &&
        state.scopeKey === scopeKey &&
        !state.loading &&
        (state.loadedAt === null ||
          Date.now() - state.loadedAt >= FOCUS_REFRESH_AFTER_MS)
      ) {
        void refresh();
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh, scopeKey, storedLoadedAt]);

  const hasEntitlement = useCallback(
    (key: string): boolean => context?.entitlements[key] === true,
    [context]
  );
  const entitlementLimit = useCallback(
    (key: string): number | null | undefined => {
      const value = context?.entitlements[key];
      return typeof value === "number" || value === null ? value : undefined;
    },
    [context]
  );

  return {
    context,
    loading,
    loaded,
    error,
    refresh,
    hasEntitlement,
    entitlementLimit,
  };
}
